/**
 * Usados — la lógica pura del módulo, sin React.
 *
 * Los iPhone son el producto principal del negocio y los usados se manejan
 * por UNIDAD: dos "15 Pro 128" con distinta batería son dos equipos distintos,
 * con distinto costo de toma y distinto precio. Acá vive la forma del
 * formulario guiado (el mismo para el alta directa, la edición y la toma en
 * parte de pago dentro de una venta), sus validaciones, y el armado de los
 * payloads. Los componentes solo pintan.
 */
import { esReferenciaUsada, formatear, type Moneda } from "@tora/shared";

import {
  armarModeloElegido,
  equipoDesdeProducto,
  esIphoneElegido,
  faltaDelModelo,
  marcaDelEquipo,
  nombreDeEquipo,
} from "@/components/iphones/equipo";
import {
  aNumero,
  type CambiosProducto,
  type EntregaUsado,
  type NuevoProducto,
  type Producto,
  type Rubro,
} from "@/lib/tipos";

// ─────────────────────────────────────────────────────────────────────────────
// El formulario
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lo que se carga de un equipo usado, en el orden en que se pregunta.
 *
 * `costo` es el costo de toma (cuánto se pagó o se reconoció por el equipo) y
 * `precio` el de reventa. El precio puede quedar vacío: en la toma en parte de
 * pago muchas veces todavía no se decidió, y el equipo queda sin publicar
 * hasta que alguien se lo ponga.
 */
/**
 * De quién es el equipo. Es la PRIMERA pregunta porque cambia todo lo demás.
 *
 * Un equipo del local se tiene en la mano: el IMEI se lee ahí mismo y es el
 * único momento en que anotarlo no cuesta nada, entra al stock como una
 * unidad, y se vende de una.
 *
 * Un equipo del proveedor todavía no se vio: no hay IMEI que copiar, no es
 * stock propio (marcarlo como tal es prometer algo que no está en el local), y
 * lo que hace falta saber es a quién llamar cuando el cliente diga que sí.
 *
 * Antes esta distinción vivía escrita adentro del nombre ("...- A PEDIDO
 * 128GB..."), donde ningún filtro la podía leer y el mostrador la ignoraba.
 */
export type OrigenUsado = "local" | "proveedor";

export const OPCIONES_ORIGEN: ReadonlyArray<{
  origen: OrigenUsado;
  titulo: string;
  detalle: string;
}> = [
  {
    origen: "local",
    titulo: "Está en el local",
    detalle: "Lo tengo en la mano. Entra al stock como una unidad y se vende de una.",
  },
  {
    origen: "proveedor",
    titulo: "Lo tiene el proveedor",
    detalle: "No es stock propio: se consigue. Se ofrece como «a pedido».",
  },
];

export interface FormUsado {
  origen: OrigenUsado;
  /** Quién lo tiene. Solo para los «a pedido»: es a quién hay que llamar. */
  proveedorId: number | null;
  modelo: string;
  memoriaGb: number | null;
  color: string;
  bateriaPct: number | null;
  imei: string;
  /**
   * No lo tengo a mano ahora: el equipo entra sin IMEI y queda marcado para
   * completarlo. Es la valvula de escape de una regla que si no la tiene se
   * termina esquivando con un numero inventado — y un IMEI inventado es peor
   * que ninguno: ocupa el lugar del real y choca con el siguiente que lo
   * invente igual.
   */
  imeiPendiente: boolean;
  cajaCable: boolean;
  garantiaMeses: number | null;
  observaciones: string;
  costo: number | null;
  moneda: Moneda;
  precio: number | null;
}

export const FORM_USADO_INICIAL: FormUsado = {
  origen: "local",
  proveedorId: null,
  modelo: "",
  memoriaGb: null,
  color: "",
  bateriaPct: null,
  imei: "",
  imeiPendiente: false,
  cajaCable: true,
  garantiaMeses: 6,
  observaciones: "",
  costo: null,
  moneda: "ARS",
  precio: null,
};

/** Un «a pedido» no está en el local: no tiene stock ni IMEI que copiar. */
export function esDeProveedor(form: FormUsado): boolean {
  return form.origen === "proveedor";
}

/**
 * Un IMEI son quince dígitos y nada más. Se guarda normalizado porque el
 * índice único de la base compara texto: "3561021510-33226" y
 * "356102151033226" son el mismo aparato para cualquiera que lo mire, pero
 * dos filas distintas para Postgres. Ya pasó una vez —el guión fue la forma
 * de esquivar el "ya está usado"— y dejó dos fichas del mismo equipo.
 */
export function normalizarImei(imei: string): string {
  return imei.replace(/\D/g, "");
}

// ─────────────────────────────────────────────────────────────────────────────
// El nombre
// ─────────────────────────────────────────────────────────────────────────────

/**
 * El nombre con el que la unidad vive en el catálogo: "IPHONE 15 PRO 128 GB
 * (USADO)".
 *
 * No lo arma esta función: lo arma `nombreDeEquipo`, que es el mismo que usan
 * el alta de sellados y el editor del catálogo. Antes cada pantalla tenía el
 * suyo y escribían la capacidad distinto —"128GB" acá, "128 GB" allá—, así que
 * el mismo teléfono cargado por dos caminos quedaba como dos productos.
 *
 * LO QUE NO ESTA EN EL NOMBRE
 * ---------------------------
 * Ni la batería ni el color. La batería porque se vuelve a medir y baja, y un
 * dato que cambia no puede ser parte del identificador: cada medición
 * renombraría el producto, y el remito de ayer terminaría diciendo otra cosa
 * que la ficha de hoy. El color por la misma razón por la que salió el "A
 * PEDIDO": adentro del nombre no lo puede leer ningún filtro ni ninguna
 * columna.
 *
 * Los dos se le suman al nombre AL MOSTRARLO (`catalogo/nombre.ts`), que es
 * donde hacen falta.
 */
export function nombreUsado(form: FormUsado): string {
  return nombreDeEquipo({ modelo: form.modelo, memoriaGb: form.memoriaGb, esUsado: true });
}

// ─────────────────────────────────────────────────────────────────────────────
// Validación por paso
// ─────────────────────────────────────────────────────────────────────────────

export const ERROR_IMEI_DIGITO = "Un dígito de ese IMEI está mal. Volvé a leerlo con *#06#.";
export const AVISO_IMEI_LARGO =
  "Ese IMEI no tiene 15 dígitos. Si es un teléfono, revisalo: todos los IMEI tienen 15.";

/**
 * El último dígito de un IMEI es un dígito verificador (Luhn): cambia si se
 * tipea mal cualquiera de los otros catorce. Con esto un error de tipeo se
 * frena en el mostrador, con el equipo en la mano, y no el día que alguien
 * busca el teléfono por su IMEI y no lo encuentra.
 */
export function imeiConDigitoValido(imei: string): boolean {
  const digitos = normalizarImei(imei);
  if (digitos.length !== 15) return false;
  let suma = 0;
  for (let i = 0; i < 15; i++) {
    let d = Number(digitos[14 - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    suma += d;
  }
  return suma % 10 === 0;
}

/**
 * La ficha activa que ya tiene ese IMEI, sin contar la que se está editando.
 * Es la misma regla que el índice único de la base (0097: solo entre activas),
 * adelantada al paso del equipo para nombrar la ficha con la que choca.
 */
export function equipoConEseImei(
  imei: string,
  productos: ReadonlyArray<Producto>,
  propioId: number | null,
): Producto | null {
  const buscado = normalizarImei(imei);
  if (!buscado) return null;
  return (
    productos.find(
      (p) => p.activo && p.id !== propioId && p.imei !== null && normalizarImei(p.imei) === buscado,
    ) ?? null
  );
}

/** Lo que la validación necesita saber de afuera para revisar el IMEI. */
export interface ContextoEquipo {
  /** Las fichas que la pantalla tiene cargadas, para el IMEI repetido. */
  productos?: ReadonlyArray<Producto>;
  /** La ficha que se está editando, que no choca consigo misma. */
  propioId?: number | null;
  /**
   * El IMEI que la ficha ya tenía guardado, para no frenar una edición o una
   * recepción que no lo tocó. Un IMEI viejo pudo cargarse mal tipeado antes de
   * que existiera este chequeo, y el dígito verificador no se le puede exigir
   * recién ahora a una ficha que nadie está corrigiendo.
   */
  imeiOriginal?: string | null;
}

function erroresDelImei(imei: string, contexto: ContextoEquipo): string[] {
  const errores: string[] = [];
  const sinCambios = imei === normalizarImei(contexto.imeiOriginal ?? "");
  if (imei.length === 15 && !imeiConDigitoValido(imei) && !sinCambios)
    errores.push(ERROR_IMEI_DIGITO);
  const repetido = equipoConEseImei(imei, contexto.productos ?? [], contexto.propioId ?? null);
  if (repetido) errores.push(`Ese IMEI ya está cargado en ${repetido.nombre}.`);
  return errores;
}

/** Lo que conviene mirar dos veces pero no frena: hay equipos que no son teléfonos. */
export function avisosEquipo(form: FormUsado): string[] {
  const imei = normalizarImei(form.imei);
  return imei.length >= 8 && imei.length !== 15 ? [AVISO_IMEI_LARGO] : [];
}

/**
 * Paso del equipo: qué es.
 *
 * El IMEI se exige cuando el equipo está en el local: es lo único que
 * distingue dos equipos iguales, y el momento de leerlo es cuando se lo tiene
 * en la mano. Pedírselo a alguien que está cargando lo que tiene el proveedor
 * sería pedirle que lo invente.
 *
 * Con `imeiPendiente` la ficha entra sin él. No es aflojar la regla: es
 * reconocer que a veces el equipo está en la caja fuerte o el cliente se lo
 * llevó a borrar, y que la alternativa real no era leerlo igual sino escribir
 * quince ceros. Sin IMEI el equipo queda marcado en la lista hasta que
 * alguien lo complete; con ceros no queda marcado nada.
 */
export function validarEquipo(form: FormUsado, contexto: ContextoEquipo = {}): string[] {
  const errores: string[] = [];
  // Un modelo a medias no es un modelo: elegir el 18 y saltear la variante deja
  // un teléfono que Apple no hizo.
  const falta = faltaDelModelo(form.modelo);
  if (falta) errores.push(falta);
  if (form.memoriaGb === null) errores.push("Elegí la memoria.");
  if (form.bateriaPct === null) errores.push("Cargá el porcentaje de batería.");
  else if (form.bateriaPct < 0 || form.bateriaPct > 100) errores.push("La batería va de 0 a 100.");

  if (esDeProveedor(form)) {
    if (form.proveedorId === null)
      errores.push(
        "¿Qué proveedor lo tiene? Es a quién hay que llamar cuando el cliente confirme.",
      );
    return errores;
  }

  const imei = normalizarImei(form.imei);
  if (form.imeiPendiente) {
    // Tildado "no lo tengo a mano" pero con algo escrito: gana lo escrito, y
    // entonces tiene que estar completo. Guardar medio IMEI y marcarlo como
    // pendiente son dos estados contradictorios de la misma ficha.
    if (imei && imei.length < 8) errores.push("Ese IMEI parece incompleto (son 15 dígitos).");
    else if (imei) errores.push(...erroresDelImei(imei, contexto));
    return errores;
  }
  if (!imei)
    errores.push(
      "Cargá el IMEI: es lo único que distingue dos equipos iguales. Si no lo tenés a mano, marcá la casilla.",
    );
  else if (imei.length < 8) errores.push("Ese IMEI parece incompleto (son 15 dígitos).");
  else errores.push(...erroresDelImei(imei, contexto));
  return errores;
}

/** Paso 2, los números: cuánto costó y a cuánto se vende. */
export function validarNumeros(form: FormUsado): string[] {
  const errores: string[] = [];
  if (form.costo === null || form.costo <= 0)
    errores.push("Falta el costo de toma: cuánto se pagó (o reconoció) por el equipo.");
  if (form.precio !== null && form.precio <= 0)
    errores.push("El precio de venta debe ser mayor a cero.");
  if (form.precio !== null && form.costo !== null && form.precio < form.costo)
    errores.push("Ojo: el precio de venta está por debajo del costo de toma.");
  return errores;
}

// ─────────────────────────────────────────────────────────────────────────────
// Payloads
// ─────────────────────────────────────────────────────────────────────────────

function ficha(
  form: FormUsado,
): Pick<
  NuevoProducto,
  "imei" | "bateria_pct" | "color" | "memoria_gb" | "caja_cable" | "garantia_meses"
> {
  return {
    imei: normalizarImei(form.imei) || null,
    bateria_pct: form.bateriaPct,
    color: form.color.trim().toUpperCase() || null,
    memoria_gb: form.memoriaGb,
    caja_cable: form.cajaCable,
    garantia_meses: form.garantiaMeses,
  };
}

/**
 * La familia que le toca a un usado.
 *
 * «Usados» es la familia de los iPHONE usados, que tienen su propia sección del
 * mostrador —armada sobre el lineup de Apple, con sus colores y sus unidades
 * colgadas del modelo—. Un usado de otra marca va a la familia de lo que ES: un
 * "SAMSUNG S22" a «Celulares», al lado del Redmi nuevo, que es donde lo busca
 * el que entra preguntando por un teléfono. Sigue siendo usado (`es_usado`, con
 * su IMEI y su batería); lo que cambia es en qué solapa se lo encuentra.
 *
 * «Celulares» y no la familia exacta porque este asistente carga teléfonos:
 * «Otra marca» pregunta marca y modelo, con Samsung, Motorola y Xiaomi
 * como botones. Si algún día entra una tablet usada habrá que corregirle la
 * familia a mano, que es mejor que adivinarla.
 *
 * El `??` final es la red: antes de quedar sin familia —una ficha sin familia no
 * cae en ninguna solapa, queda cargada e invisible— vuelve a «Usados».
 */
export function rubroDeUsado(form: FormUsado, rubros: ReadonlyArray<Rubro>): number | null {
  const deUsados = rubros.find((r) => r.nombre === "Usados")?.id ?? null;
  // Lo decide lo que se eligio, no el nombre: si no se toco un iPhone del
  // lineup, es de otra marca.
  if (esIphoneElegido(form.modelo)) return deUsados;
  return rubros.find((r) => r.nombre === "Celulares")?.id ?? deUsados;
}

/**
 * Alta directa: el producto que se crea.
 *
 * El stock NO entra acá: lo hace un ingreso aparte, y solo para los del local.
 * Un «a pedido» queda en cero a propósito — con `a_pedido` en true el sistema
 * lo muestra como «A pedido» en vez de «Sin stock», que es exactamente la
 * diferencia entre "lo consigo" y "no lo tengo".
 */
export function aNuevoUsado(form: FormUsado, rubroId: number | null): NuevoProducto {
  return {
    nombre: nombreUsado(form),
    marca: marcaDelEquipo(form.modelo),
    descripcion: form.observaciones.trim() || null,
    rubro_id: rubroId,
    proveedor_id: esDeProveedor(form) ? form.proveedorId : null,
    precio_moneda: form.moneda,
    precio_manual: form.precio,
    costo: form.costo ?? 0,
    costo_moneda: form.moneda,
    publicado: false,
    es_usado: true,
    a_pedido: esDeProveedor(form),
    ...ficha(form),
  };
}

/** Edición: solo lo que cambió respecto de la ficha original. */
export function aCambiosUsado(form: FormUsado, original: Producto): CambiosProducto {
  const nuevo = aNuevoUsado(form, original.rubro_id);
  const cambios: CambiosProducto = {};
  if (nuevo.nombre && nuevo.nombre !== original.nombre) cambios.nombre = nuevo.nombre;
  if ((nuevo.descripcion ?? null) !== (original.descripcion ?? null))
    cambios.descripcion = nuevo.descripcion;
  if (nuevo.imei !== original.imei) cambios.imei = nuevo.imei;
  if (nuevo.bateria_pct !== original.bateria_pct) cambios.bateria_pct = nuevo.bateria_pct;
  if (nuevo.color !== original.color) cambios.color = nuevo.color;
  if (nuevo.memoria_gb !== original.memoria_gb) cambios.memoria_gb = nuevo.memoria_gb;
  if (nuevo.caja_cable !== original.caja_cable) cambios.caja_cable = nuevo.caja_cable;
  if (nuevo.garantia_meses !== original.garantia_meses)
    cambios.garantia_meses = nuevo.garantia_meses;
  if (nuevo.a_pedido !== original.a_pedido) cambios.a_pedido = nuevo.a_pedido;
  if ((nuevo.proveedor_id ?? null) !== (original.proveedor_id ?? null))
    cambios.proveedor_id = nuevo.proveedor_id ?? null;
  const costoOriginal = aNumero(original.costo);
  if (
    form.costo !== null &&
    (form.costo !== costoOriginal || form.moneda !== original.costo_moneda)
  ) {
    cambios.costo = form.costo;
    cambios.costo_moneda = form.moneda;
  }
  const manualOriginal = original.precio_manual === null ? null : aNumero(original.precio_manual);
  if (form.precio !== manualOriginal || form.moneda !== original.precio_moneda) {
    cambios.precio_manual = form.precio;
    cambios.precio_moneda = form.moneda;
  }
  return cambios;
}

/** Toma en parte de pago: lo que viaja adentro de la venta. */
export function aEntrega(form: FormUsado): EntregaUsado {
  return {
    nombre: nombreUsado(form),
    // El servidor decide la solapa con esto, no leyendo el nombre (0109).
    es_iphone: esIphoneElegido(form.modelo),
    ...(marcaDelEquipo(form.modelo) ? { marca: marcaDelEquipo(form.modelo)! } : {}),
    valor_toma: form.costo ?? 0,
    moneda: form.moneda,
    ...(normalizarImei(form.imei) ? { imei: normalizarImei(form.imei) } : {}),
    ...(form.bateriaPct !== null ? { bateria_pct: form.bateriaPct } : {}),
    ...(form.color.trim() ? { color: form.color.trim().toUpperCase() } : {}),
    ...(form.memoriaGb !== null ? { memoria_gb: form.memoriaGb } : {}),
    caja_cable: form.cajaCable,
    ...(form.garantiaMeses !== null ? { garantia_meses: form.garantiaMeses } : {}),
    ...(form.precio !== null ? { precio_venta: form.precio } : {}),
    ...(form.observaciones.trim() ? { observaciones: form.observaciones.trim() } : {}),
  };
}

/**
 * Edición: reconstruye el formulario desde la ficha guardada.
 *
 * El modelo no tiene columna —vive adentro del nombre armado—, así que se lee
 * de vuelta con `equipoDesdeProducto`, que es el mismo lector que usa el editor
 * del catálogo. Un modelo que el lineup no conoce (un Samsung) vuelve tal cual
 * y el selector lo muestra en «Otro»: editarle el precio no puede obligar a
 * convertirlo en un iPhone.
 */
export function desdeProductoUsado(p: Producto): FormUsado {
  const equipo = equipoDesdeProducto(p);
  return {
    origen: p.a_pedido ? "proveedor" : "local",
    proveedorId: p.proveedor_id ?? null,
    modelo: armarModeloElegido(equipo),
    memoriaGb: equipo.memoriaGb,
    color: p.color ?? "",
    bateriaPct: p.bateria_pct,
    imei: p.imei ?? "",
    imeiPendiente: faltaImei(p),
    cajaCable: p.caja_cable ?? true,
    garantiaMeses: p.garantia_meses,
    observaciones: p.descripcion ?? "",
    costo: aNumero(p.costo) > 0 ? aNumero(p.costo) : null,
    moneda: p.precio_moneda,
    precio: p.precio_manual === null ? null : aNumero(p.precio_manual),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Revisión y lista
// ─────────────────────────────────────────────────────────────────────────────

/** El resumen del paso de revisión, en palabras del que lo lee. */
export function resumenUsado(form: FormUsado, nombreProveedor?: string | null): string[] {
  const lineas = [nombreUsado(form)];
  const detalle: string[] = [];
  // El nombre lleva solo modelo y memoria: la batería y el color, que se
  // eligieron en los pasos anteriores, no se veían al confirmar.
  if (form.color.trim()) detalle.push(`color ${form.color.trim()}`);
  if (form.bateriaPct !== null) detalle.push(`batería ${form.bateriaPct} %`);
  const imei = normalizarImei(form.imei);
  if (imei) detalle.push(`IMEI ${imei}`);
  else if (!esDeProveedor(form)) detalle.push("sin IMEI todavía");
  detalle.push(form.cajaCable ? "con caja y cable" : "sin caja y cable");
  if (form.garantiaMeses) detalle.push(`garantía ${form.garantiaMeses} meses`);
  lineas.push(detalle.join(", "));
  lineas.push(
    esDeProveedor(form)
      ? `Lo tiene ${nombreProveedor?.trim() || "el proveedor"}: queda «a pedido», sin stock propio.`
      : "Está en el local: entra al stock como una unidad.",
  );
  if (form.costo !== null)
    lineas.push(
      `${esDeProveedor(form) ? "Costo del proveedor" : "Costo de toma"}: ${formatear(form.costo, form.moneda)}`,
    );
  lineas.push(
    form.precio !== null
      ? `Precio de venta: ${formatear(form.precio, form.moneda)}`
      : "Sin precio de venta todavía: no se ve en la tienda hasta que se lo pongas.",
  );
  if (!esDeProveedor(form) && !normalizarImei(form.imei))
    lineas.push(
      "El IMEI queda pendiente: el equipo aparece marcado en la lista hasta que lo cargues.",
    );
  return lineas;
}

/** El equipo lo tiene el proveedor: se consigue, no está en el local. */
export function esAPedido(p: Producto): boolean {
  return p.a_pedido;
}

/**
 * El orden del mostrador: lo que TENGO, después lo que CONSIGO, después lo
 * que ya se fue. Mezclar los tres esconde la mitad de lo que se puede ofrecer.
 */
function rangoDisponibilidad(p: Producto): number {
  if (aNumero(p.stock_actual) > 0) return 0;
  if (p.a_pedido) return 1;
  return 2;
}

export function ordenarUsados(productos: Producto[]): Producto[] {
  return [...productos].sort((a, b) => {
    const da = rangoDisponibilidad(a);
    const db = rangoDisponibilidad(b);
    if (da !== db) return da - db;
    return a.nombre.localeCompare(b.nombre, "es");
  });
}

/**
 * Se vendió: tenía unidad y ya no la tiene.
 *
 * Un «a pedido» también está en cero, pero nunca estuvo en el local: decirle
 * "Vendido" sería inventar una venta que no ocurrió. Un precio de referencia
 * tampoco: no es ningún equipo, es cuánto vale el modelo. Mientras las
 * referencias vivían en su propio bloque esto no hacía falta; ahora comparten
 * lista con las unidades y sin el reparo todas dirían "Vendido".
 */
export function estaVendido(p: Producto): boolean {
  return !p.a_pedido && !esReferencia(p) && aNumero(p.stock_actual) <= 0;
}

/**
 * Nadie le puso precio de venta todavía.
 *
 * OJO: no alcanza con mirar `precio_venta` — con costo cargado y markup 0, el
 * trigger deja precio_venta = costo, y eso NO es un precio decidido: es el
 * equipo ofreciéndose al costo de toma, margen cero. Lo decidido es
 * `precio_manual`.
 */
export function sinPrecio(p: Producto): boolean {
  return p.precio_manual === null;
}

/**
 * Un precio de referencia, no una unidad: los "(USADO)" genéricos de la vieja
 * lista de precios ("IPHONE 13 128 GB (USADO)"), que dicen cuánto vale un
 * modelo usado sin ser ningún equipo en particular. Se los reconoce porque no
 * tienen ficha: ni batería ni IMEI — una unidad real siempre trae al menos la
 * batería (el asistente la exige).
 */
export function esReferencia(p: Producto): boolean {
  // La regla vive en @tora/shared para que la base (es_unidad_usada, 0092), la
  // web y esta pantalla contesten lo mismo.
  return esReferenciaUsada(p);
}

/**
 * Una unidad real del local a la que le falta el IMEI.
 *
 * Es el otro lado de `esReferencia`: las dos miran fichas sin IMEI, y lo que
 * las separa es la batería. Con batería hay un equipo concreto que alguien
 * tuvo en la mano y no pudo leer; sin batería es un precio de modelo, que no
 * tiene IMEI porque no es ningún equipo. Un «a pedido» tampoco cuenta: no se
 * lo vio nunca, así que no hay nada pendiente de anotar.
 */
export function faltaImei(p: Producto): boolean {
  return p.es_usado && !p.a_pedido && p.imei === null && p.bateria_pct !== null;
}

/**
 * El detalle de la unidad: lo que NO está en el nombre ni en una columna.
 *
 * Capacidad y color están en el título, y la batería tiene su columna; ponerlos
 * también acá era decir tres veces lo mismo en la misma fila. Queda lo que no
 * se ve de otra manera, y por eso el detalle se abre apretando la fila en vez
 * de ocupar una segunda línea en las 29: se consulta cuando se busca un equipo
 * puntual, no cuando se recorre la lista.
 */
export function detalleUsado(p: Producto): Array<{ etiqueta: string; valor: string }> {
  const lineas: Array<{ etiqueta: string; valor: string }> = [];
  if (p.imei) lineas.push({ etiqueta: "IMEI", valor: p.imei });
  else if (!p.a_pedido && p.bateria_pct !== null)
    lineas.push({ etiqueta: "IMEI", valor: "sin cargar todavía" });
  lineas.push({
    etiqueta: "Caja y cable",
    valor: p.caja_cable === false ? "sin caja ni cable" : "con caja y cable",
  });
  if (p.garantia_meses) lineas.push({ etiqueta: "Garantía", valor: `${p.garantia_meses} meses` });
  if (p.descripcion?.trim())
    lineas.push({ etiqueta: "Observaciones", valor: p.descripcion.trim() });
  return lineas;
}

/**
 * Lo que le falta a la ficha para estar completa, en palabras.
 *
 * No es un estado del equipo sino trabajo pendiente, y por eso la lista lo
 * muestra como un contador y no como un chip: recorriendo hace falta saber que
 * a esta fila le falta algo; QUE le falta se lee abriendo.
 *
 * Un vendido no entra: el IMEI que no se anoto ya no se puede anotar, y marcar
 * para siempre algo que no tiene arreglo es ruido permanente. Una referencia
 * tampoco: no es un equipo, no hay IMEI que cargarle.
 */
export function pendientesUsado(p: Producto): string[] {
  if (estaVendido(p) || esReferencia(p)) return [];
  const faltan: string[] = [];
  if (faltaImei(p)) faltan.push("Falta el IMEI");
  if (!esAPedido(p) && sinPrecio(p)) faltan.push("Sin precio de venta");
  return faltan;
}

/**
 * En que paso del asistente esta lo que le falta a esta ficha.
 *
 * Editar un equipo marcado como incompleto abria el asistente en el paso uno,
 * con todo en blanco por recorrer, y el dato que faltaba —el motivo por el que
 * se entro— aparecia dos pantallas despues. El que abre ya sabe que hay algo
 * mal: la pantalla tiene que empezar ahi.
 *
 * El orden es el del asistente: si faltan las dos cosas se entra por la
 * primera, y la segunda aparece al avanzar.
 */
export function pasoDeLoQueFalta(p: Producto): "equipo" | "numeros" | null {
  if (pendientesUsado(p).length === 0) return null;
  if (faltaImei(p)) return "equipo";
  if (sinPrecio(p)) return "numeros";
  return null;
}
